<template>
  <div ref="highlightLayer" class="highlight-layer"></div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue';
import * as d3 from 'd3';
import { cleanx,
  isLastDayOfMonth, 
  getBavariaHolidaysBetween, 
  dayMs,
  getWidthPerDay
} from "./planner.js";

// 1. ADD PROPS: This is crucial for reactivity
const props = defineProps({
  startDate: { type: Date, required: true },
  endDate: { type: Date, required: true },
  configs: { type: Array, required: true },
  isprintView: { type: Boolean, required: true}
});

const highlightLayer = ref(null);

import {useViewStore} from "@/stores/viewStore"
const view = useViewStore();

function renderHighlight() {
  const widthPerDay = getWidthPerDay(props.isprintView, view.zoomWidthPerDay)
  if (!highlightLayer.value || isNaN(props.startDate.getTime())) return;

  // 2. CLEAR PREVIOUS: Remove old highlights before drawing new ones
  highlightLayer.value.innerHTML = '';

  const days = d3.utcDay.range(props.startDate, props.endDate);
  const holidays = getBavariaHolidaysBetween(props.startDate, props.endDate);
  const totalDays = Math.round((props.endDate - props.startDate) / dayMs);
  const x = d3.scaleUtc()
    .domain([props.startDate, props.endDate])
    .range([0, totalDays * widthPerDay]);

  days.forEach((day, idx) => {
    const isWeekend = day.getUTCDay() === 0 || day.getUTCDay() === 6;
    const isHoliday = holidays.some(h => h.date.toDateString() === day.toDateString());
    const isLdm = isLastDayOfMonth(day);

    // Create the background highlight (Weekend/Holiday)
    if (isWeekend || (isHoliday && !props.isprintView)) {
      let div = document.createElement("div");
      div.className = `highlight ${isWeekend ? 'weekend' : 'holiday'}`;
      div.style.left = `${cleanx(x, day)}px`;
      div.style.width = `${widthPerDay}px`;
      highlightLayer.value.appendChild(div);
    }

    // Create the vertical separators (Month/Week ends)
    let offset = 2;
    if (props.isprintView) {
      let div = document.createElement("div");
 		  div.classList.add("printview")
 		  div.classList.add("highlight")
 		  div.classList.add("day")
      if (day.getUTCDay() === 0) {
 		    div.classList.add("last_day_week")
      } else if (isLdm) {
 		    div.classList.add("last_day_month")
      }

      div.style.left = `${cleanx(x, day) + offset}px`;
      if (idx === 0) {
        div.classList.add("first_day")
        div.style.left = `${cleanx(x, day)}px`;
      }
      div.style.width = `${widthPerDay}px`;
      highlightLayer.value.appendChild(div);
    } else if (isLdm || day.getUTCDay() === 0) {
      let div = document.createElement("div");
 		  div.classList.add("highlight")
 		  div.classList.add("day")
      if (isLdm) {
 		    div.classList.add("last_day_month")
      } else {
 		    div.classList.add("last_day_week")
      }
      // Dieselben Versaetze wie die Linien in Timeaxis.vue (Woche 1 px, Monat
      // 0 px), sonst standen Achse und Planer 1-2 px gegeneinander (TIM-34).
      // Mit 1 px sitzt der Wochenstrich genau auf der Tagesgrenze. Der Druck
      // oben behaelt seine eigenen Werte - das PDF-Raster ist darauf abgestimmt.
      const bildschirmOffset = isLdm ? 0 : 1;
      div.style.left = `${cleanx(x, day) + bildschirmOffset}px`;
      div.style.width = `${widthPerDay}px`;
      highlightLayer.value.appendChild(div);
    }
  });
}

// 3. WATCH: Redraw if the parent changes the dates
watch(() => [props.startDate, props.endDate], renderHighlight);

onMounted(renderHighlight);
</script>

<style>
.highlight-layer {
  pointer-events: none;
}

.day {
  	border-right: 1px solid black !important;  
}

</style>
